import React, { useState } from 'react';

import classNames from 'classnames';
import confirm from 'components/confirm';
import { faRotate } from '@fortawesome/pro-solid-svg-icons';

import ContactDetails from 'bundles/backoffice/contacts/details';
import ContactFormModal from 'bundles/backoffice/contacts/forms/modal';
import Dropdown from 'components/dropdown';
import ReplaceContactModal from 'bundles/backoffice/contacts/replace_modal';
import { ContactsCardContactIcon } from './contact_icon';

import type { Contact, ContactRole } from 'bundles/backoffice/contacts/types';

export interface Props {
  className?: string;
  confirmDeletion?: boolean;
  contactRole: ContactRole;
  features: Record<string, boolean>;
  forRecord?: { id: number, type: string };
  hasSiblingAbove?: boolean;
  hasSiblingBelow?: boolean;
  readOnly?: boolean;
  showRoleLabel?: boolean;
  onChangeContactRole: (contactRole: ContactRole, changes: Partial<ContactRole>) => void;
  onRemoveContactRole: (contactRole: ContactRole) => void;
  onSaveContact: (contact: Contact) => void;
}

export function ContactsCardContactRole({
  className,
  confirmDeletion,
  contactRole,
  features,
  forRecord,
  hasSiblingAbove,
  hasSiblingBelow,
  readOnly,
  showRoleLabel = true,
  onChangeContactRole,
  onRemoveContactRole,
  onSaveContact,
}: Props) {
  const [showContactEditModal, setShowContactEditModal] = useState(false);
  const [showReplaceContactModal, setShowReplaceContactModal] = useState(false);

  const { contact } = contactRole;

  function deleteContact() {
    if (confirmDeletion) {
      confirm('Are you sure you want to remove this contact?', {
        buttonLabel: 'Remove',
        buttonStyle: 'danger',
        title: `Remove Contact`
      }).then(() => {
        onRemoveContactRole(contactRole);
      });
    } else {
      onRemoveContactRole(contactRole);
    }
  }

  return (
    <div className={classNames('position-relative d-flex gap-2 py-4 justify-content-center w-100', className)}>
      <ContactsCardContactIcon
        contactRole={contactRole}
        hasSiblingAbove={hasSiblingAbove}
        hasSiblingBelow={hasSiblingBelow}
      />

      <ContactDetails
        contact={contact}
        contactRole={showRoleLabel ? contactRole : null}
        renderLinks={features.edgeCrm}
        showEmployer={false}
      />

      {!readOnly &&
        <Dropdown>
          <Dropdown.Item isEdit onClick={() => setShowContactEditModal(true)} />
          <Dropdown.Item icon={faRotate} label="Replace" onClick={() => setShowReplaceContactModal(true)}/>
          <Dropdown.Item isDelete label="Remove" onClick={deleteContact} />
        </Dropdown>
      }

      <ContactFormModal
        allowTagging={features.crmTags}
        contact={contact}
        contactType={contact.contactType}
        forceSaveAsRoot
        forRecord={forRecord}
        multipleBusinesses
        show={showContactEditModal}
        warn
        onCancel={() => setShowContactEditModal(false)}
        onSave={(contact) => {
          onSaveContact(contact);
          setShowContactEditModal(false);
        }}
      />

      <ReplaceContactModal
        contactRole={contactRole}
        newContactVisibility="shared"
        show={showReplaceContactModal}
        onChange={newContactRole => onChangeContactRole(contactRole, newContactRole)}
        onHide={() => setShowReplaceContactModal(false)}
      />
    </div>
  );
}
